import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { cx, Icon } from '@amumin/ui-core';

import styles from './Dialog.module.css';
import { closeToastLayer, DialogToasts, openToastLayer } from './Toast.js';

export interface DialogProps {
  open: boolean;
  /** Sluiten via X, Escape of een tik buiten het venster. */
  onClose: () => void;
  title: string;
  /** Eén zin uitleg onder de titel. */
  description?: string;
  children?: ComponentChildren;
  /** Knoppen onderaan; de primaire actie staat rechts (op mobiel onderaan, volle breedte). */
  actions?: ComponentChildren;
  /** dialog = gecentreerd venster; sheet = paneel van onderen (mobiel) of rechts (breed). */
  variant?: 'dialog' | 'sheet';
  /** Toegankelijke naam van de sluitknop (i18n `design.dialog.close`). */
  closeLabel: string;
  /** Voor destructieve vragen: niet sluiten bij een tik op de achtergrond. */
  dismissible?: boolean;
  name?: string;
  /**
   * Golf 2 (oprichter #11): een sheet die uitklapt tot volledig scherm (knop naast Sluiten). De
   * inhoud blijft staan (zelfde element, alleen een andere vorm); Escape en Sluiten werken gewoon.
   */
  expand?: {
    expanded: boolean;
    edgeToggle?: boolean;
    onToggle: () => void;
    /** Toegankelijke naam: "Volledig scherm" / "Kleiner maken". */
    expandLabel: string;
    collapseLabel: string;
  };
  /**
   * Oprichter 1 okt: elke sheet krijgt standaard de randknop naar volledig scherm (zie
   * configureSheetExpand). Alleen waar volledig scherm geen zin heeft (een kort menu of een korte
   * waarschuwing) zet een sheet dit op false.
   */
  expandable?: boolean;
}

/** Standaard voor elke sheet: teksten en de opslagsleutel per gebruiker (de app zet dit één keer). */
export interface SheetExpandDefaults {
  expandLabel: () => string;
  collapseLabel: () => string;
  /** Sleutel in localStorage voor deze sheet, bv. fb.sheet.full.<identiteit>.<naam>. */
  storageKey: (name: string) => string;
}

let sheetDefaults: SheetExpandDefaults | null = null;

/** Zet (of wist met null) de standaard-uitklapknop voor alle sheets met een naam. */
export function configureSheetExpand(defaults: SheetExpandDefaults | null): void {
  sheetDefaults = defaults;
}

function readFull(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFull(key: string, on: boolean): void {
  try {
    localStorage.setItem(key, on ? '1' : '0');
  } catch {
    // geen opslag (privémodus): dan onthouden we het niet
  }
}

/**
 * Modaal venster op het native <dialog>-element (focus-trap, Escape en toplaag van de browser).
 * De ouderapp draait op moderne browsers (§12.2); de kiosk gebruikt dit nooit.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  actions,
  variant = 'dialog',
  closeLabel,
  dismissible = true,
  name,
  expand: expandProp,
  expandable = true,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Meldingen (showToast) staan zolang dit venster open is in het venster zelf (Toast.tsx).
  const [layer, setLayer] = useState<number | null>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const hasActions = Boolean(actions);
  // Standaard-uitklapknop: de keuze staat in localStorage (per gebruiker en per sheet) en wordt bij
  // elke render gelezen; wisselen schrijft hem weg en tekent opnieuw.
  const [, redraw] = useState(0);
  const defaults = sheetDefaults;
  const autoKey =
    !expandProp && expandable && variant === 'sheet' && defaults && name
      ? defaults.storageKey(name)
      : null;
  let expand = expandProp;
  if (autoKey && defaults) {
    const on = readFull(autoKey);
    expand = {
      expanded: on,
      edgeToggle: true,
      onToggle: () => {
        writeFull(autoKey, !on);
        redraw((n) => n + 1);
      },
      expandLabel: defaults.expandLabel(),
      collapseLabel: defaults.collapseLabel(),
    };
  }

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
    if (!open) return undefined;
    const id = openToastLayer();
    setLayer(id);
    return () => {
      closeToastLayer(id);
      setLayer(null);
    };
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onCancel = (e: Event) => {
      e.preventDefault();
      closeRef.current();
    };
    el.addEventListener('cancel', onCancel);
    return () => el.removeEventListener('cancel', onCancel);
  }, []);

  // Sheet: meldingen staan boven de knoppenbalk (niet eroverheen: review c-ux r3 zag de toast
  // over Bewerken). De hoogte van die balk gaat als --dialog-actions-h naar de CSS.
  useEffect(() => {
    const el = ref.current;
    const bar = actionsRef.current;
    if (!open || variant !== 'sheet' || !el) return undefined;
    const measure = () =>
      el.style.setProperty('--dialog-actions-h', `${bar ? bar.offsetHeight : 0}px`);
    measure();
    if (!bar || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [open, variant, hasActions]);

  const titleId = name ? `${name}-title` : undefined;
  const descId = name && description ? `${name}-desc` : undefined;

  return (
    <dialog
      ref={ref}
      class={cx(
        styles.dialog,
        variant === 'sheet' && styles.sheet,
        expand?.edgeToggle && styles.withEdge,
        expand?.expanded === true && styles.full,
        autoKey && styles.autoExpand,
      )}
      data-name={name}
      data-expanded={expand ? (expand.expanded ? 'true' : 'false') : undefined}
      aria-labelledby={titleId}
      aria-describedby={descId}
      onClick={(e) => {
        // Klik op de ::backdrop komt binnen als klik op het dialog-element zelf.
        if (dismissible && e.target === ref.current) closeRef.current();
      }}
    >
      {expand?.edgeToggle ? (
        <button
          type="button"
          class={styles.edgeExpand}
          aria-label={expand.expanded ? expand.collapseLabel : expand.expandLabel}
          title={expand.expanded ? expand.collapseLabel : expand.expandLabel}
          aria-pressed={expand.expanded ? 'true' : 'false'}
          data-name={name ? `${name}-edge-expand` : undefined}
          onClick={expand.onToggle}
        >
          <Icon id={expand.expanded ? 'chevron-right' : 'chevron-left'} size={20} />
        </button>
      ) : null}
      <div class={styles.panel}>
        <div class={styles.head}>
          <div class={styles.headText}>
            <h2 id={titleId} class={styles.title}>
              {title}
            </h2>
            {description ? (
              <p id={descId} class={styles.description}>
                {description}
              </p>
            ) : null}
          </div>
          {expand ? (
            <button
              type="button"
              class={cx(styles.close, styles.expand)}
              aria-label={expand.expanded ? expand.collapseLabel : expand.expandLabel}
              title={expand.expanded ? expand.collapseLabel : expand.expandLabel}
              aria-pressed={expand.expanded ? 'true' : 'false'}
              data-name={name ? `${name}-expand` : undefined}
              onClick={() => expand.onToggle()}
            >
              <Icon id={expand.expanded ? 'minimize-2' : 'maximize-2'} size={20} />
            </button>
          ) : null}
          <button
            type="button"
            class={styles.close}
            aria-label={closeLabel}
            onClick={() => closeRef.current()}
          >
            <Icon id="x" size={20} />
          </button>
        </div>
        {children ? <div class={styles.body}>{children}</div> : null}
        {actions ? (
          <div ref={actionsRef} class={styles.actions}>
            {actions}
          </div>
        ) : null}
      </div>
      <DialogToasts
        layer={layer}
        sheet={variant === 'sheet'}
        onSpace={(px) => ref.current?.style.setProperty('--dialog-toast-h', `${px}px`)}
      />
    </dialog>
  );
}

/**
 * Blad (docs/ux/ontwerpsysteem.md): een Dialog als sheet, van onderen op de telefoon en rechts op
 * de computer, met een vaste kop (titel en Sluiten blijven staan) en de knoppen onderaan vast.
 */
export function Sheet(props: Omit<DialogProps, 'variant'>) {
  return <Dialog {...props} variant="sheet" />;
}
